import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuthStore } from "../../store/useAuthStore";
import { UserRound, Wallet2, Receipt } from "../../utils/icons";
import UserProfileModal from "./UserProfileModal";
import BetHistoryModal from "./BetHistoryModal";

const DEFAULT_AVATAR = "//img2.distributedresourcestorage.com/avatar/57268122/s?t=0";

// Turnover custom SVG (matches old frontend exactly)
const TurnoverIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
    <path d="M12 2v4l4 4-4 4v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M8 12h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    <circle cx="12" cy="12" r="2" fill="currentColor"/>
  </svg>
);

// PNG icon wrapper (matches old frontend StyledIcon with filter: brightness(0) invert(1))
const ImgIcon = ({ src, alt }) => (
  <img
    src={src}
    alt={alt}
    style={{ width: 18, height: 18, filter: "brightness(0) invert(1)" }}
  />
);

const menuItems = [
  { type: "lucide", Icon: UserRound, label: "User Information", action: "userInfo" },
  { type: "lucide", Icon: Receipt, label: "Transactions", path: "/transactions" },
  { type: "img", src: "/assets/images/nanoicons/transactions.png", label: "Bet History", action: "betHistory" },
  { type: "svg", label: "Turnover", path: "/turnover" },
  { type: "img", src: "/assets/images/nanoicons/liveSupport.png", label: "Live Support", action: "support" },
  { type: "lucide", Icon: Wallet2, label: "Wallet", path: "/wallet" },
  { type: "img", src: "/assets/images/nanoicons/nanoswap.png", label: "Swap", path: "/swap" },
  { type: "img", src: "/assets/images/normal-seetings.png", label: "Global Settings", path: "/global-settings" },
];

const ProfileMenu = () => {
  const [open, setOpen] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showBetHistory, setShowBetHistory] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleNav = (item) => {
    setOpen(false);
    if (item.action === "support") return; // TODO: open crisp chat
    if (item.action === "userInfo") { setShowUserModal(true); return; }
    if (item.action === "betHistory") { setShowBetHistory(true); return; }
    navigate(item.path);
  };

  const handleLogout = () => {
    setOpen(false);
    logout();
    window.location.reload();
  };

  const avatar = user?.avatar || DEFAULT_AVATAR;

  const renderIcon = (item) => {
    if (item.type === "lucide") {
      const IconComp = item.Icon;
      return <IconComp size={18} color="#fff" />;
    }
    if (item.type === "img") {
      return <ImgIcon src={item.src} alt={item.label} />;
    }
    if (item.type === "svg") {
      return <TurnoverIcon />;
    }
    return null;
  };

  return (
    <div className="pm-wrapper" ref={ref}>
      <div className="pm-trigger" onClick={() => setOpen((v) => !v)}>
        <img
          className="pm-avatar"
          src={avatar}
          alt="Profile"
          onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
        />
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="pm-dropdown"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
          >
            {/* Menu items */}
            {menuItems.map((item, i) => (
              <motion.div
                key={item.label}
                className="pm-menu-item"
                onClick={() => handleNav(item)}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.03, duration: 0.15 }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 18, height: 18, marginRight: 7 }}>
                  {renderIcon(item)}
                </div>
                <span>{item.label}</span>
              </motion.div>
            ))}

            <div className="pm-divider" />

            {/* Logout */}
            <motion.div
              className="pm-menu-item pm-logout"
              onClick={handleLogout}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: menuItems.length * 0.03, duration: 0.15 }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 18, height: 18, marginRight: 7 }}>
                <img
                  src="/assets/images/nanoicons/logout.png"
                  alt="Logout"
                  style={{ width: 18, height: 18, filter: "brightness(0) invert(1)" }}
                />
              </div>
              <span style={{ color: "var(--color-accent-coral)" }}>Logout</span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <UserProfileModal show={showUserModal} onClose={() => setShowUserModal(false)} />
      <BetHistoryModal show={showBetHistory} onClose={() => setShowBetHistory(false)} />
    </div>
  );
};

export default ProfileMenu;
